/**
 * OaSIS Explorer Frontend - TEER Tag Component
 * 
 * Displays a TEER level tag/chip with icon and short description
 */

import React from 'react';
import { getTeerDescriptionShort } from '../../utils/occupationUtils';
import { getTeerIcon as getTeerIconFromConstants } from '../../utils/occupationConstants';

interface TeerTagProps {
  oasisCode: string;
  className?: string;
  size?: 'sm' | 'md';
  variant?: 'default' | 'header';
}

/**
 * TEER tag component
 * Displays the short TEER description with icon in a styled badge
 */
export const TeerTag: React.FC<TeerTagProps> = ({
  oasisCode,
  className = '',
  size = 'md',
  variant = 'default',
}) => {
  const teerDescription = getTeerDescriptionShort(oasisCode);
  
  if (!teerDescription) {
    return null;
  }

  // Extract TEER level (second digit of OaSIS code)
  const teerLevel = oasisCode.charAt(1);
  const iconSize = size === 'sm' ? 10 : 12;
  const iconElement = getTeerIconFromConstants(teerLevel, iconSize);
  // Clone icon with updated className for header variant
  const icon = variant === 'header' && React.isValidElement(iconElement)
    ? React.cloneElement(iconElement as React.ReactElement<any>, {
        className: "text-white/90"
      })
    : iconElement;

  // Size-based styling
  const sizeClasses = size === 'sm'
    ? 'gap-0.5 px-1.5 py-0.5 text-xs rounded'
    : 'gap-1 px-2.5 py-1 text-xs rounded-md';

  // Variant-based styling
  const variantClasses = variant === 'header'
    ? 'bg-white/20 backdrop-blur text-white/90'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';

  return (
    <div className={`inline-flex items-center ${sizeClasses} ${variantClasses} font-medium ${className}`}>
      {icon}
      <span>{teerDescription} TEER</span>
    </div>
  );
};

